import React, { useState } from "react";
import SearchBar from "./SearchBar";
import ConsultantCard from "./ConsultantCard";
import Header from "./Header";

interface Consultant {
  id: string;
  cv_id?: number;
  firstname: string;
  lastname: string;
  email: string;
  cv_title?: string;
  main_skills?: { name: string, functional_skills: string[] }[];
  sector?: string;
}

const HomePage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [searchResults, setSearchResults] = useState<Consultant[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const parseSearchQuery = (query: string) => {

    if (query.includes('+')) {
      const parts = query.split('+').map(p => p.trim()).filter(p => p.length > 0);
      return {
        query: parts.join(' '),
        filters: {}
      };
    } else {
      return { query: query, filters: {} };
    }
  };

  const handleSearch = async (query: string, filters: any) => {
    const API_URL = process.env.REACT_APP_API_URL;
    
    if (!query.trim() && !filters.skills && !filters.sector && !filters.location && !filters.name && !filters.job_title) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const parsed = parseSearchQuery(query);
      const finalFilters = { ...parsed.filters, ...filters };
      const params = new URLSearchParams();
      if (parsed.query.trim()) params.append("query", parsed.query);
      if (finalFilters.skills) params.append("skills", finalFilters.skills);
      if (finalFilters.sector) params.append("sector", finalFilters.sector);
      if (finalFilters.location) params.append("location", finalFilters.location);
      if (finalFilters.name) params.append("name", finalFilters.name);
      if (finalFilters.job_title) params.append("job_title", finalFilters.job_title);

      const response = await fetch(
        `${API_URL}/consultant-form/search-global?${params.toString()}`
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.consultants || []);
      } else {
        console.error("Erreur lors de la recherche");
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Erreur réseau:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: window.innerWidth < 768 ? "1rem 0.75rem" : "2rem 1rem",
        }}
      >
        {/* Hero Section */}
        <div
          style={{
            textAlign: "center",
            marginBottom: window.innerWidth < 768 ? "2rem" : "3rem",
            animation: "fadeIn 0.6s ease-out",
          }}
        >
          <h1
            style={{
              fontSize: window.innerWidth < 480 ? "1.75rem" : window.innerWidth < 768 ? "2rem" : "2.5rem",
              fontWeight: 700,
              color: "#1E3C73",
              marginBottom: "1rem",
              letterSpacing: "-0.02em",
            }}
          >
            Bienvenue sur JLCTalents
          </h1>
          <p
            style={{
              fontSize: "1.125rem",
              color: "#64748b",
              maxWidth: "600px",
              margin: "0 auto 2rem",
            }}
          >
            Accédez aux compétences internes ou créez votre CV.
          </p>

          {!hasSearched && (
            <p
              style={{
                fontSize: "1.25rem",
                fontWeight: 500,
                color: "#1E3C73",
                marginTop: "2rem",
              }}
            >
              Que souhaitez-vous faire aujourd'hui ?
            </p>
          )}
        </div>

        {/* Two Main Action Blocks - Only show when no search has been performed */}
        {!hasSearched && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(auto-fit, minmax(320px, 1fr))",
              gap: window.innerWidth < 768 ? "1.5rem" : "2rem",
              marginBottom: window.innerWidth < 768 ? "2rem" : "3rem",
            }}
          >
            {/* Block 1: Créer mon CV (warm color - orange) */}
            <div
              style={{
                background: "linear-gradient(135deg, #FF9100 0%, #ffb347 100%)",
                padding: window.innerWidth < 768 ? "2rem 1.5rem" : "3rem 2rem",
                borderRadius: "20px",
                boxShadow: "0 8px 32px rgba(255, 145, 0, 0.25)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: "320px",
              }}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'form' } }));
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-8px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 48px rgba(255, 145, 0, 0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(255, 145, 0, 0.25)";
              }}
            >
              <div style={{ fontSize: "4rem", marginBottom: "1.5rem", textAlign: "center" }}>✍️</div>
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  marginBottom: "1rem",
                  textAlign: "center",
                }}
              >
                Créer mon CV
              </h2>
              <p style={{ fontSize: "1.125rem", opacity: 0.95, textAlign: "center", marginBottom: "2rem" }}>
                Créez et complétez votre CV en quelques clics
              </p>
              <button
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "2px solid white",
                  color: "white",
                  padding: "0.875rem 2rem",
                  borderRadius: "12px",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  alignSelf: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.color = "#FF9100";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "white";
                }}
              >
                Commencer →
              </button>
            </div>

            {/* Block 2: Rechercher un CV (cold color - blue) */}
            <div
              style={{
                background: "linear-gradient(135deg, #1E3C73 0%, #2a5298 100%)",
                padding: window.innerWidth < 768 ? "2rem 1.5rem" : "3rem 2rem",
                borderRadius: "20px",
                boxShadow: "0 8px 32px rgba(30, 60, 115, 0.25)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: window.innerWidth < 768 ? "280px" : "320px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-8px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 48px rgba(30, 60, 115, 0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(30, 60, 115, 0.25)";
              }}
            >
              <div style={{ fontSize: "4rem", marginBottom: "1.5rem", textAlign: "center" }}>🔍</div>
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  marginBottom: "1rem",
                  textAlign: "center",
                }}
              >
                Rechercher un CV
              </h2>
              <p style={{ fontSize: "1rem", opacity: 0.9, textAlign: "center", marginBottom: "0.5rem" }}>
                Recherchez par compétences, nom de consultant ou secteur d'activité
              </p>
              <p style={{ 
                fontSize: "0.85rem", 
                opacity: 0.8, 
                textAlign: "center", 
                marginBottom: "1.5rem",
                fontStyle: "italic"
              }}>
                💡 Combinez vos critères avec <strong>+</strong> (ex: <strong>delphine + paris</strong> ou <strong>powerbi + banque</strong>)
              </p>
              
              {/* Integrated search bar */}
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Compétences, nom, secteur..."
                  className="search-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const query = (e.target as HTMLInputElement).value;
                      if (query.trim()) {
                        handleSearch(query, {});
                      }
                    }
                  }}
                />
                <button
                  className="search-button"
                  onClick={(e) => {
                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                    const query = input?.value || '';
                    if (query.trim()) {
                      handleSearch(query, {});
                    }
                  }}
                >
                  Rechercher
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {hasSearched && (
          <div style={{ marginTop: "3rem" }}>
            {/* Back button */}
            <button
              style={{
                background: "transparent",
                border: "2px solid #1E3C73",
                color: "#1E3C73",
                padding: "0.75rem 1.5rem",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "2rem",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onClick={() => {
                setHasSearched(false);
                setSearchResults([]);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#1E3C73";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#1E3C73";
              }}
            >
              ← Nouvelle recherche
            </button>
            
            {isSearching ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    width: "48px",
                    height: "48px",
                    border: "4px solid #e5e7eb",
                    borderTopColor: "#FF9100",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                ></div>
                <p
                  style={{
                    marginTop: "1rem",
                    color: "#64748b",
                    fontSize: "1rem",
                  }}
                >
                  Recherche en cours...
                </p>
              </div>
            ) : searchResults.length > 0 ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1.5rem",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      color: "#1E3C73",
                    }}
                  >
                    Résultats de recherche
                  </h2>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      background: "#f1f5f9",
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                      fontWeight: 500,
                    }}
                  >
                    {searchResults.length} consultant
                    {searchResults.length > 1 ? "s" : ""} trouvé
                    {searchResults.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  {searchResults.map((consultant) => (
                    <ConsultantCard key={consultant.id} consultant={consultant} />
                  ))}
                </div>
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  background: "white",
                  borderRadius: "16px",
                  boxShadow: "0 4px 24px rgba(30, 60, 115, 0.08)",
                }}
              >
                <div
                  style={{
                    fontSize: "4rem",
                    marginBottom: "1rem",
                  }}
                >
                  🔍
                </div>
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1E3C73",
                    marginBottom: "0.5rem",
                  }}
                >
                  Aucun résultat trouvé
                </h3>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "1rem",
                  }}
                >
                  Essayez avec d'autres mots-clés ou filtres
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
